/**
 * VideoReviewDialog — watch the finished clip, approve it or ask for changes,
 * and download a file ready to post.
 *
 * The download endpoint needs the caller's token, so the file is fetched once
 * and held as a blob: the same blob plays in the player and saves to disk, so
 * "Download" is one click with nothing to set up.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Loader2, Download, CheckCircle2, MessageSquareWarning, Film, Clock,
} from 'lucide-react';

const cleanName = (title, format) =>
  `${(title || 'clip').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`
  + `${format ? `-${format}` : ''}.mp4`;

export default function VideoReviewDialog({ projectId, presets = [], onClose, onChanged }) {
  const qc = useQueryClient();
  const [outputId, setOutputId] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [changes, setChanges] = useState('');
  const [askingChanges, setAskingChanges] = useState(false);

  const { data: project, refetch } = useQuery({
    queryKey: ['video-project', projectId],
    queryFn: () => api.get(`/video/projects/${projectId}`).then(r => r.data),
    enabled: !!projectId,
  });

  const outputs = project?.output_files || [];
  const current = useMemo(
    () => outputs.find(o => o.id === outputId) || outputs[outputs.length - 1],
    [outputs, outputId],
  );
  const preset = presets.find(p => p.key === (current?.social_format || project?.social_format));

  // Fetch the rendered file once, with the token the API client already holds.
  useEffect(() => {
    let revoke;
    if (!current?.id || !projectId) return undefined;
    setLoadingFile(true);
    setBlobUrl(null);
    api.get(`/video/projects/${projectId}/output/${current.id}/download`,
      { responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setBlobUrl(url);
      })
      .catch((e) => toast.error(`Could not load the video: ${getErrorMessage(e)}`))
      .finally(() => setLoadingFile(false));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [projectId, current?.id]);

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = cleanName(project?.title, current?.social_format || project?.social_format);
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Saved — ready to post');
  };

  const setStatus = useMutation({
    mutationFn: ({ status, comment }) => api.post(
      `/video/projects/${projectId}/status`,
      { status, review_comment: comment || undefined }).then(r => r.data),
    onSuccess: (_d, vars) => {
      toast.success(vars.status === 'approved'
        ? 'Approved — ready to publish'
        : 'Sent back to the editor with your notes');
      setChanges(''); setAskingChanges(false);
      refetch();
      qc.invalidateQueries({ queryKey: ['video-projects'] });
      onChanged?.();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const status = project?.status;
  const comments = project?.comments || [];

  return (
    <Dialog open={!!projectId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto"
                     data-testid="video-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <Film className="h-4 w-4 text-fuchsia-600" />
            {project?.title || 'Loading…'}
            {status && (
              <Badge variant="outline" className="text-[10px]" data-testid="review-status-badge">
                {status === 'review' ? 'waiting for review' : status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Watch it through, then approve it or send it back with notes. The download is already
            sized for {preset?.label || 'social media'}.
          </DialogDescription>
        </DialogHeader>

        {outputs.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="review-no-output">
            This project has no rendered video yet — open the editor and render one first.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-black overflow-hidden flex items-center justify-center min-h-[220px]">
              {loadingFile ? (
                <span className="text-white/70 text-sm inline-flex items-center gap-2"
                      data-testid="review-loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading the finished video…
                </span>
              ) : blobUrl ? (
                <video src={blobUrl} controls playsInline className="w-full max-h-[55vh]"
                       data-testid="review-player" />
              ) : (
                <span className="text-white/60 text-sm">Video unavailable</span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={download} disabled={!blobUrl}
                      className="bg-fuchsia-600 hover:bg-fuchsia-700"
                      data-testid="review-download-btn">
                <Download className="h-4 w-4 mr-1.5" />
                Download for social
              </Button>
              <span className="text-xs text-slate-500" data-testid="review-file-meta">
                {preset ? `${preset.label} · ${preset.width}×${preset.height} · ` : ''}
                {Math.max(1, Math.round((current?.size_bytes || 0) / 1024 / 1024))} MB · MP4
              </span>

              {status === 'review' && (
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setAskingChanges((v) => !v)}
                          data-testid="review-request-changes-btn">
                    <MessageSquareWarning className="h-4 w-4 mr-1.5" /> Request changes
                  </Button>
                  <Button onClick={() => setStatus.mutate({ status: 'approved' })}
                          disabled={setStatus.isPending}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="review-approve-btn">
                    {setStatus.isPending
                      ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                    Approve
                  </Button>
                </div>
              )}
              {status === 'approved' && (
                <Badge className="ml-auto bg-emerald-100 text-emerald-800 text-[10px]"
                       data-testid="review-approved-badge">
                  Approved — download and post it
                </Badge>
              )}
            </div>

            {askingChanges && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2"
                   data-testid="review-changes-box">
                <Label className="text-xs">What needs changing?</Label>
                <Textarea value={changes} onChange={(e) => setChanges(e.target.value)}
                          rows={3} placeholder="e.g. trim the first 4 seconds and move the caption up"
                          data-testid="review-changes-text" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAskingChanges(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" disabled={!changes.trim() || setStatus.isPending}
                          onClick={() => setStatus.mutate({ status: 'editing', comment: changes })}
                          data-testid="review-send-changes-btn">
                    Send back to the editor
                  </Button>
                </div>
              </div>
            )}

            {outputs.length > 1 && (
              <div className="flex flex-wrap gap-1.5" data-testid="review-output-switcher">
                {outputs.map((o, i) => (
                  <Button key={o.id} size="sm"
                          variant={o.id === current?.id ? 'default' : 'outline'}
                          onClick={() => setOutputId(o.id)}
                          data-testid={`review-output-${o.id}`}>
                    {o.label || `Render ${i + 1}`}
                  </Button>
                ))}
              </div>
            )}

            {comments.length > 0 && (
              <div className="space-y-1.5" data-testid="review-comments">
                <Label className="text-xs">Review notes</Label>
                <div className="divide-y rounded-lg border border-slate-200">
                  {comments.slice().reverse().map((c) => (
                    <div key={c.id} className="p-2.5" data-testid={`review-comment-${c.id}`}>
                      <p className="text-sm text-slate-800">{c.comment}</p>
                      <p className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {c.action} · {c.user_email || 'a team member'}
                        {c.at ? ` · ${String(c.at).slice(0, 16).replace('T', ' ')}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
